import React from 'react';
import { STATUS_SELECT_OPTIONS, STATUS_LABEL_BY_INDEX } from '../../constants/projectStatus';
import { PROJECT_TYPES, projectTypeLabel } from '../../constants/projectTypes';
import { SHINGLE_BRAND_NAMES, SHINGLE_PROFILES, shingleLinesFor } from '../../constants/shingles';
import { contactDisplayName } from '../../utils/contactName';
import { formatPhone } from '../../utils/phone';
import useLeadSources from '../../utils/useLeadSources';
import { CLASSIFICATION_LABELS } from '../../constants/classifications';
import { boardPreferencesAPI } from '../../services/api';
import CustomFieldValues from '../CustomFields/CustomFieldValues';

const classLabels = CLASSIFICATION_LABELS;

const CLASSIFICATION_OPTIONS = classLabels.map((label, index) => ({ value: index, label }));
const TYPE_OPTIONS = PROJECT_TYPES.map(t => ({ value: t.index, label: t.label }));
const YES_NO_OPTIONS = [{ value: false, label: 'No' }, { value: true, label: 'Yes' }];
const PROFILE_OPTIONS = SHINGLE_PROFILES.map(p => ({ value: p, label: p }));

const formatCurrency = (val) => {
    if (val == null) return null;
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (val) => {
    if (!val) return null;
    return new Date(val).toLocaleDateString();
};

// The API sends full ISO timestamps; <input type="date"> wants yyyy-MM-dd.
const toDateInput = (val) => (val ? new Date(val).toISOString().split('T')[0] : '');

const Field = ({ label, value, empty = '—' }) => (
    <div className="drawer-field">
        <span className="drawer-field-label">{label}</span>
        <span className={`drawer-field-value${!value ? ' empty' : ''}`}>
            {value || empty}
        </span>
    </div>
);

// Like Field, but with a one-tap copy button — for customer contact details.
const CopyField = ({ label, value, empty = '—' }) => {
    const [copied, setCopied] = React.useState(false);
    const doCopy = async () => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(String(value));
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            /* clipboard unavailable (non-secure context) — no-op */
        }
    };
    return (
        <div className="drawer-field">
            <span className="drawer-field-label">{label}</span>
            <span className={`drawer-field-value${!value ? ' empty' : ''}`}>
                {value || empty}
                {value && (
                    <button
                        type="button"
                        className="drawer-copy-btn"
                        onClick={doCopy}
                        title={`Copy ${label.toLowerCase()}`}
                        aria-label={`Copy ${label.toLowerCase()}`}
                    >
                        <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                )}
            </span>
        </div>
    );
};

// A drawer row that edits in place. Click the value, change it, done — the whole
// point is that the common edits on a job never need the Edit Project modal.
//
// `kind` picks the input. Selects and checkboxes commit on change (there's
// nothing to type, so waiting for a blur just adds a step); typed inputs commit
// on Enter or blur, and Escape abandons. `onSave` throws on failure, which is
// how the row knows to put the old value back.
const EditableField = ({
    label,
    value,
    kind = 'text',
    options = [],
    datalist = [],
    display,
    empty = '—',
    // Whether the select offers a "clear it" entry. Off for the fields the
    // backend requires a value for (status, type, classification).
    allowEmpty = true,
    onSave,
    parse = (v) => (v === '' ? null : v),
}) => {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const inputRef = React.useRef(null);
    const listId = React.useId();

    const commitOnChange = kind === 'select' || kind === 'bool';

    const begin = () => {
        if (saving) return;
        setDraft(value ?? '');
        setEditing(true);
    };

    React.useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current.select) inputRef.current.select();
        }
    }, [editing]);

    const commit = async (raw) => {
        const next = parse(raw);
        // Nothing typed / picked differently — close without a round-trip.
        if (next === (value ?? null) || (next == null && (value ?? '') === '')) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(next);
            setEditing(false);
        } catch {
            // The parent surfaces the message; just restore the shown value.
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const shown = display !== undefined ? display : value;

    if (!editing) {
        return (
            <div className="drawer-field">
                <span className="drawer-field-label">{label}</span>
                <button
                    type="button"
                    className={`drawer-field-value drawer-field-editable${!shown ? ' empty' : ''}`}
                    onClick={begin}
                    disabled={saving}
                    title={`Edit ${label.toLowerCase()}`}
                >
                    {saving ? <i className="fas fa-spinner fa-spin"></i> : (shown || empty)}
                    <i className="fas fa-pen drawer-field-pen"></i>
                </button>
            </div>
        );
    }

    return (
        <div className="drawer-field">
            <span className="drawer-field-label">{label}</span>
            <span className="drawer-field-value">
                {commitOnChange ? (
                    <select
                        ref={inputRef}
                        className="drawer-field-input"
                        value={kind === 'bool' ? String(draft ?? false) : (draft ?? '')}
                        onChange={(e) => commit(e.target.value)}
                        onBlur={() => setEditing(false)}
                        disabled={saving}
                    >
                        {kind === 'select' && allowEmpty && <option value="">— none —</option>}
                        {(kind === 'bool' ? YES_NO_OPTIONS : options).map(o => (
                            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                        ))}
                    </select>
                ) : kind === 'textarea' ? (
                    <textarea
                        ref={inputRef}
                        className="drawer-field-input drawer-field-textarea"
                        value={draft ?? ''}
                        rows={3}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={(e) => commit(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
                        disabled={saving}
                    />
                ) : (
                    <>
                        <input
                            ref={inputRef}
                            className="drawer-field-input"
                            type={kind === 'date' ? 'date' : kind === 'number' ? 'number' : 'text'}
                            step={kind === 'number' ? '0.01' : undefined}
                            list={datalist.length ? listId : undefined}
                            value={draft ?? ''}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={(e) => commit(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                                if (e.key === 'Escape') setEditing(false);
                            }}
                            disabled={saving}
                        />
                        {datalist.length > 0 && (
                            <datalist id={listId}>
                                {datalist.map(d => <option key={d} value={d} />)}
                            </datalist>
                        )}
                    </>
                )}
            </span>
        </div>
    );
};

// Sections a user can hide. Presentation only: this changes what someone looks
// at, never what they are allowed to see, so it must not be read as a permission.
// A field tech wants the repair fields and nothing else; a sales rep has no use
// for the roofing spec.
const DRAWER_SECTIONS = [
    { key: 'status', label: 'Status' },
    { key: 'repair', label: 'Repair' },
    { key: 'jobSite', label: 'Job Site' },
    { key: 'customer', label: 'Customer' },
    { key: 'tenant', label: 'Tenant' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'keyDates', label: 'Key Dates' },
    { key: 'financial', label: 'Financial' },
    { key: 'roofing', label: 'Roofing Details' },
];

const DetailsDrawer = ({ project, users = [], crews = [], onSave, onStatusChange }) => {
    const [hiddenSections, setHiddenSections] = React.useState([]);
    const [showSectionPicker, setShowSectionPicker] = React.useState(false);
    const prefsLoaded = React.useRef(false);

    // The admin-managed list, same source the project form uses — so a source Trey
    // adds shows up in both places rather than only in the one that was wired.
    const leadSourceOptions = useLeadSources();

    // Four live automations match Lead Source as an exact string, and imported jobs
    // can hold a value that predates the list. Keeping the current value as an option
    // means opening this dropdown cannot silently blank a job's source just because
    // nobody has added it to the admin table yet.
    const leadSourceChoices = React.useMemo(() => {
        const current = (project?.leadSource ?? '').trim();
        if (!current || leadSourceOptions.some(o => o.value === current)) return leadSourceOptions;
        return [...leadSourceOptions, { value: current, label: `${current} (existing)` }];
    }, [leadSourceOptions, project?.leadSource]);

    React.useEffect(() => {
        let cancelled = false;
        boardPreferencesAPI.get('projectDetail')
            .then(res => {
                if (cancelled) return;
                setHiddenSections(res.data?.hiddenSections || []);
            })
            .catch(() => { /* preferences are optional — a failure just shows everything */ })
            .finally(() => { prefsLoaded.current = true; });
        return () => { cancelled = true; };
    }, []);

    const isSectionHidden = (key) => hiddenSections.includes(key);

    const toggleSection = (key) => {
        const next = hiddenSections.includes(key)
            ? hiddenSections.filter(k => k !== key)
            : [...hiddenSections, key];
        setHiddenSections(next);
        if (!prefsLoaded.current) return;
        boardPreferencesAPI.save({ hiddenSections: next }, 'projectDetail')
            .catch(() => { /* a lost preference is not worth interrupting the job for */ });
    };

    if (!project) return null;

    const customer = project.customer;
    const salesRep = project.salesRep;
    const statusText = STATUS_LABEL_BY_INDEX[project.status] || 'Unknown';
    const statusClass = `status-${statusText.toLowerCase().replace(/\s+/g, '-')}`;

    // Read-only when the page didn't hand down a save function, so the drawer
    // still renders correctly anywhere it's reused.
    const editable = typeof onSave === 'function';
    const save = (field) => (value) => onSave(field, value);

    // Dates come back from the input as yyyy-MM-dd; the API wants an ISO string.
    const parseDate = (v) => (v === '' ? null : new Date(v).toISOString());
    const parseNumber = (v) => (v === '' ? null : Number(v));
    const parseInteger = (v) => (v === '' ? null : parseInt(v, 10));
    const parseBool = (v) => v === 'true';

    const addons = [];
    if (project.gutters) addons.push('Gutters');
    if (project.skylights) addons.push('Skylights');
    if (project.gutterGuards) addons.push('Gutter Guards');

    const pickups = [];
    if (project.materialPickupMARS) pickups.push('MARS');
    if (project.materialPickupSRS) pickups.push('SRS');
    if (project.materialPickupABC) pickups.push('ABC');

    // Job flags. Pill row only renders if at least one is true.
    const flags = [];
    if (project.changeOrder) flags.push({ label: 'Change Order', color: 'var(--accent-primary-light)' });
    if (project.isNotRoofRelated) flags.push({ label: 'NRR', color: '#9a3412' });

    const userOptions = users.map(u => ({ value: u.id, label: u.username }));

    // Installer: ONE field, writing the foreign keys that actually do something.
    //
    // There used to be two — a free-text "Current Installer" and the scheduled
    // crew/installer keys — and they could disagree about who was on the job.
    // Setting the text field looked right and sent nothing: work-order texts,
    // photo routing and assignment notifications all resolve through the keys,
    // and nothing that sends a text ever read the text field.
    //
    // A job carries a crew OR an individual, never both (the API rejects both), so
    // one picker holds both kinds, namespaced "crew:3" / "user:7" and split back
    // on save.
    const installerPickerOptions = [
        ...crews
            // A retired crew still on this job stays listed — dropping it would
            // blank the field and clear the assignment on the next save.
            .filter(c => c.isActive || String(c.id) === String(project.scheduledCrewId))
            .map(c => ({
                value: `crew:${c.id}`,
                label: `${c.name}${c.leadName ? ` — ${c.leadName}` : ''}${c.isActive ? '' : ' (inactive)'}`
                    + (c.leadPhone ? '' : ' · no phone'),
            })),
        ...users.map(u => ({
            value: `user:${u.id}`,
            label: `${u.firstName || u.username}${u.phone ? '' : ' · no phone'}`,
        })),
    ];

    const installerValue = project.scheduledCrewId
        ? `crew:${project.scheduledCrewId}`
        : project.scheduledInstallerId
            ? `user:${project.scheduledInstallerId}`
            : '';

    const installerLabel = installerPickerOptions.find(o => o.value === installerValue)?.label ?? null;

    // Writes BOTH keys at once: setting either clears the other, so the mutual
    // exclusion the API enforces cannot be violated from here, and there is no
    // moment where the record holds both.
    const saveInstaller = (raw) => {
        if (!raw) return onSave({ scheduledCrewId: null, scheduledInstallerId: null });
        const [kind, id] = String(raw).split(':');
        const parsed = parseInt(id, 10);
        return kind === 'crew'
            ? onSave({ scheduledCrewId: parsed, scheduledInstallerId: null })
            : onSave({ scheduledCrewId: null, scheduledInstallerId: parsed });
    };

    // Repair notes are the whole job on a Repair Log, so show that section for
    // repair work — and anywhere the fields already have content, so nothing
    // typed earlier becomes invisible after a type change.
    const showRepair = project.type === 0
        || project.repairWorkOrderText
        || project.repairDescription
        || project.repairCompletionNotes;

    return (
        <div className="pd-details-drawer">
            <div className="drawer-sections-bar">
                <button
                    type="button"
                    className="drawer-sections-trigger"
                    onClick={() => setShowSectionPicker(v => !v)}
                    title="Choose which sections to show"
                >
                    <i className="fas fa-sliders"></i> Sections
                    {hiddenSections.length > 0 && (
                        <span className="drawer-sections-count">{hiddenSections.length} hidden</span>
                    )}
                </button>

                {showSectionPicker && (
                    <div className="drawer-sections-menu">
                        {DRAWER_SECTIONS.map(sec => (
                            <label key={sec.key} className="drawer-sections-item">
                                <input
                                    type="checkbox"
                                    checked={!isSectionHidden(sec.key)}
                                    onChange={() => toggleSection(sec.key)}
                                />
                                {sec.label}
                            </label>
                        ))}
                        <div className="drawer-sections-note">
                            Only changes what you see. Everyone else's view is unaffected.
                        </div>
                    </div>
                )}
            </div>

            {/* Status Section */}
            <div className="drawer-section" data-section="status" hidden={isSectionHidden('status')}>
                <h4 className="drawer-section-title">Status</h4>
                {editable ? (
                    <EditableField
                        label="Status"
                        kind="select"
                        value={project.status}
                        display={<span className={`drawer-badge status-badge ${statusClass}`}>{statusText}</span>}
                        options={STATUS_SELECT_OPTIONS}
                        allowEmpty={false}
                        // Status has its own endpoint (it fires automations and
                        // stamps StatusChangedAt), so it doesn't go through onSave.
                        onSave={(v) => onStatusChange(v)}
                        parse={parseInteger}
                        empty="Unknown"
                    />
                ) : (
                    <Field
                        label="Status"
                        value={<span className={`drawer-badge status-badge ${statusClass}`}>{statusText}</span>}
                    />
                )}
                {editable ? (
                    <>
                        <EditableField
                            label="Type"
                            kind="select"
                            value={project.type}
                            display={projectTypeLabel(project.type)}
                            options={TYPE_OPTIONS}
                            allowEmpty={false}
                            onSave={save('type')}
                            parse={parseInteger}
                        />
                        <EditableField
                            label="Classification"
                            kind="select"
                            value={project.classification}
                            display={classLabels[project.classification]}
                            options={CLASSIFICATION_OPTIONS}
                            allowEmpty={false}
                            onSave={save('classification')}
                            parse={parseInteger}
                        />
                        <EditableField
                            label="Sales Rep"
                            kind="select"
                            value={project.salesRepId ?? ''}
                            display={salesRep ? salesRep.username : null}
                            options={userOptions}
                            onSave={save('salesRepId')}
                            parse={parseInteger}
                        />
                        {/* A dropdown, not free text. Typed by hand it was one
                            misspelling away from matching none of the automations that
                            key on it — "Property Managment" reads fine and routes
                            nothing. */}
                        <EditableField
                            label="Lead Source"
                            kind="select"
                            value={project.leadSource}
                            options={leadSourceChoices}
                            onSave={save('leadSource')}
                        />
                        <EditableField
                            label="Installer"
                            kind="select"
                            value={installerValue}
                            display={installerLabel}
                            options={installerPickerOptions}
                            onSave={saveInstaller}
                            parse={(v) => v}
                            empty="Unassigned"
                        />
                    </>
                ) : (
                    <>
                        <Field label="Type" value={projectTypeLabel(project.type)} />
                        <Field label="Classification" value={classLabels[project.classification]} />
                        <Field label="Sales Rep" value={salesRep ? salesRep.username : null} />
                        <Field label="Lead Source" value={project.leadSource} />
                        <Field label="Installer" value={installerLabel} empty="Unassigned" />
                    </>
                )}
                {flags.length > 0 && (
                    <div className="drawer-field">
                        <span className="drawer-field-label">Flags</span>
                        <span className="drawer-field-value">
                            <span className="drawer-addons">
                                {flags.map(f => (
                                    <span
                                        key={f.label}
                                        className="drawer-addon-tag"
                                        style={{ background: f.color, color: '#fff', borderColor: f.color }}
                                    >
                                        {f.label}
                                    </span>
                                ))}
                            </span>
                        </span>
                    </div>
                )}
            </div>

            {/* Repair — the tech's two fields, first in the drawer on a repair job
                so neither needs hunting for. */}
            {showRepair && editable && (
                <div className="drawer-section" data-section="repair" hidden={isSectionHidden('repair')}>
                    <h4 className="drawer-section-title">Repair</h4>
                    {/* Top of the section: this is what the installer was texted, and
                        reading it used to mean opening Edit and scrolling the whole
                        form. Editable in place for the same reason — dispatch details
                        change by phone, and a correction should not need the modal. */}
                    <EditableField
                        label="Work Order"
                        kind="textarea"
                        value={project.repairWorkOrderText}
                        onSave={save('repairWorkOrderText')}
                        empty="Add what the installer is being sent to do…"
                    />
                    {/* Both numbers live here rather than only in the edit modal.
                        Aaron's point: entering what he is owed meant opening Edit and
                        scrolling past the whole form to reach one field. They are the
                        two amounts on a repair — what the customer pays, and what the
                        installer is paid — so they belong where the repair is read. */}
                    <EditableField
                        label="Repair Price"
                        kind="number"
                        value={project.repairPrice ?? ''}
                        display={formatCurrency(project.repairPrice)}
                        onSave={save('repairPrice')}
                        parse={parseNumber}
                        empty="What the customer pays…"
                    />
                    <EditableField
                        label="Repair Labor"
                        kind="number"
                        value={project.repairLabor ?? ''}
                        display={formatCurrency(project.repairLabor)}
                        onSave={save('repairLabor')}
                        parse={parseNumber}
                        empty="What the installer is paid…"
                    />
                    <EditableField
                        label="Reported Issue"
                        kind="textarea"
                        value={project.repairDescription}
                        onSave={save('repairDescription')}
                        empty="Add what the homeowner reported…"
                    />
                    <EditableField
                        label="Work Done"
                        kind="textarea"
                        value={project.repairCompletionNotes}
                        onSave={save('repairCompletionNotes')}
                        empty="Add what was done…"
                    />
                    <EditableField
                        label="Not Roof Related"
                        kind="bool"
                        value={project.isNotRoofRelated}
                        display={project.isNotRoofRelated ? 'Yes' : 'No'}
                        onSave={save('isNotRoofRelated')}
                        parse={parseBool}
                    />
                    {project.isNotRoofRelated && (
                        <EditableField
                            label="NRR Notes"
                            kind="textarea"
                            value={project.notRoofRelatedNotes}
                            onSave={save('notRoofRelatedNotes')}
                        />
                    )}
                </div>
            )}

            {/* Job Site + Customer. Two sections, so wrapped in a fragment. */}
            {customer && (
                <>
                <div className="drawer-section" data-section="jobSite" hidden={isSectionHidden('jobSite')}>
                    <h4 className="drawer-section-title">Job Site</h4>
                    {/* First, because it is how the job is referred to. Editable in
                        place like the other job-site fields. */}
                    <EditableField
                        label="Property Name"
                        value={project.propertyName}
                        onSave={save('propertyName')}
                    />
                    {/* The job's own address, falling back to the contact's property
                        when it was never set. They differ on property-management work:
                        the job is at the building, the contact is the management
                        company. Showing the contact's address unlabelled as "the
                        address" is what made PM jobs unusable. */}
                    <CopyField
                        label="Address"
                        value={
                            [
                                project.jobSiteStreet,
                                project.jobSiteLine2,
                                project.jobSiteCity,
                                [project.jobSiteState, project.jobSiteZip].filter(Boolean).join(' '),
                            ].filter(Boolean).join(', ')
                            || [
                                customer.propertyStreet,
                                customer.propertyCity,
                                [customer.propertyState, customer.propertyZipCode].filter(Boolean).join(' '),
                            ].filter(Boolean).join(', ')
                        }
                    />
                    {!project.jobSiteStreet && (
                        <div className="drawer-note">Using the contact's address — no job site set.</div>
                    )}
                </div>

                <div className="drawer-section" data-section="customer" hidden={isSectionHidden('customer')}>
                    <h4 className="drawer-section-title">Customer</h4>
                    <CopyField label="Name" value={contactDisplayName(customer)} />
                    <CopyField label="Phone" value={formatPhone(customer.phone)} />
                    <CopyField label="Email" value={customer.email} />
                    {/* "Contact Address", not "Property". This is where the contact is
                        — on managed work, the agency's office — and the Job Site section
                        above has its own Address for where the work happens. Both were
                        labelled "Property", which is the one labelling mistake
                        guaranteed to send someone to the wrong building. */}
                    <CopyField
                        label="Contact Address"
                        value={[
                            customer.propertyStreet,
                            customer.propertyCity,
                            [customer.propertyState, customer.propertyZipCode].filter(Boolean).join(' '),
                        ].filter(Boolean).join(', ')}
                    />
                </div>
                </>
            )}

            {/* Tenant. On property-management work the Customer above is the managing
                agency, so the person actually at the property is recorded here. The
                typed-in name and number are worth showing because they are the only
                copy: a tenant saved as a contact can be found again from Contacts, but
                one typed straight into the project modal appears nowhere else. */}
            {(project.tenantName || project.tenantContact || project.tenantCustomerId) && (
                <div className="drawer-section" data-section="tenant" hidden={isSectionHidden('tenant')}>
                    <h4 className="drawer-section-title">Tenant</h4>
                    {project.tenantName && <CopyField label="Name" value={project.tenantName} />}
                    {project.tenantContact && (
                        <CopyField label="Contact" value={formatPhone(project.tenantContact)} />
                    )}
                    {project.tenantCustomerId && !project.tenantName && !project.tenantContact && (
                        <div className="drawer-note">
                            Linked to a saved contact — their details live on that contact record.
                        </div>
                    )}
                </div>
            )}

            {/* Insurance / Claims. Always shown when editable — otherwise there's
                no way to enter the first value without the modal. */}
            {(editable || project.insuranceCarrier || project.claimNumber || project.dateOfLoss) && (
                <div className="drawer-section" data-section="insurance" hidden={isSectionHidden('insurance')}>
                    <h4 className="drawer-section-title">Insurance</h4>
                    {editable ? (
                        <>
                            <EditableField label="Carrier" value={project.insuranceCarrier} onSave={save('insuranceCarrier')} />
                            <EditableField label="Claim #" value={project.claimNumber} onSave={save('claimNumber')} />
                            <EditableField
                                label="Date of Loss"
                                kind="date"
                                value={toDateInput(project.dateOfLoss)}
                                display={formatDate(project.dateOfLoss)}
                                onSave={save('dateOfLoss')}
                                parse={parseDate}
                            />
                        </>
                    ) : (
                        <>
                            <Field label="Carrier" value={project.insuranceCarrier} />
                            <Field label="Claim #" value={project.claimNumber} />
                            <Field label="Date of Loss" value={formatDate(project.dateOfLoss)} />
                        </>
                    )}
                </div>
            )}

            {/* Key Dates. Created is server-owned and stays read-only; the rest
                auto-advance the status when filled in for the first time. */}
            <div className="drawer-section" data-section="keyDates" hidden={isSectionHidden('keyDates')}>
                <h4 className="drawer-section-title">Key Dates</h4>
                <Field label="Created" value={formatDate(project.createdAt)} />
                {[
                    ['Estimate', 'estimateDate'],
                    ['Contract Signed', 'contractSignedDate'],
                    ['Scheduled Start', 'scheduledStartDate'],
                    ['Completion', 'completionDate'],
                ].map(([label, field]) => (
                    editable ? (
                        <EditableField
                            key={field}
                            label={label}
                            kind="date"
                            value={toDateInput(project[field])}
                            display={formatDate(project[field])}
                            onSave={save(field)}
                            parse={parseDate}
                        />
                    ) : (
                        <Field key={field} label={label} value={formatDate(project[field])} />
                    )
                ))}
            </div>

            {/* Financial */}
            <div className="drawer-section" data-section="financial" hidden={isSectionHidden('financial')}>
                <h4 className="drawer-section-title">Financial</h4>
                {[
                    ['Estimated', 'estimatedCost'],
                    ['Final', 'finalCost'],
                    ['Paid', 'amountPaid'],
                ].map(([label, field]) => (
                    editable ? (
                        <EditableField
                            key={field}
                            label={label}
                            kind="number"
                            value={project[field] ?? ''}
                            display={formatCurrency(project[field])}
                            onSave={save(field)}
                            parse={parseNumber}
                        />
                    ) : (
                        <Field key={field} label={label} value={formatCurrency(project[field])} />
                    )
                ))}
            </div>

            {/* Roofing Details */}
            <div className="drawer-section" data-section="roofing" hidden={isSectionHidden('roofing')}>
                <h4 className="drawer-section-title">Roofing Details</h4>
                {editable ? (
                    <>
                        <EditableField
                            label="Brand"
                            value={project.shingleBrand}
                            datalist={SHINGLE_BRAND_NAMES}
                            onSave={save('shingleBrand')}
                        />
                        {/* "Shingle Type" is the product line; the backend field named
                            shingleType holds the profile and shows below. */}
                        <EditableField
                            label="Shingle Type"
                            value={project.shingleLine}
                            datalist={shingleLinesFor(project.shingleBrand)}
                            onSave={save('shingleLine')}
                        />
                        <EditableField
                            label="Profile"
                            kind="select"
                            value={project.shingleType ?? ''}
                            options={PROFILE_OPTIONS}
                            onSave={save('shingleType')}
                        />
                        <EditableField label="Shingle Color" value={project.shingleColor} onSave={save('shingleColor')} />
                        <EditableField
                            label="Metal Work"
                            kind="bool"
                            value={project.hasMetalWork}
                            display={project.hasMetalWork ? 'Yes' : 'No'}
                            onSave={save('hasMetalWork')}
                            parse={parseBool}
                        />
                        {project.hasMetalWork && (
                            <EditableField
                                label="Metal Details"
                                kind="textarea"
                                value={project.metalWorkDescription}
                                onSave={save('metalWorkDescription')}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <Field label="Brand" value={project.shingleBrand} />
                        <Field label="Shingle Type" value={project.shingleLine} />
                        <Field label="Profile" value={project.shingleType} />
                        <Field label="Shingle Color" value={project.shingleColor} />
                        <Field label="Metal Work" value={project.hasMetalWork ? 'Yes' : 'No'} />
                        {project.hasMetalWork && project.metalWorkDescription && (
                            <Field label="Metal Details" value={project.metalWorkDescription} />
                        )}
                    </>
                )}
                {addons.length > 0 && (
                    <div className="drawer-field">
                        <span className="drawer-field-label">Add-ons</span>
                        <span className="drawer-field-value">
                            <span className="drawer-addons">
                                {addons.map(a => <span key={a} className="drawer-addon-tag">{a}</span>)}
                            </span>
                        </span>
                    </div>
                )}
                {pickups.length > 0 && (
                    <div className="drawer-field">
                        <span className="drawer-field-label">Material Pickup</span>
                        <span className="drawer-field-value">
                            <span className="drawer-addons">
                                {pickups.map(p => <span key={p} className="drawer-pickup-tag">{p}</span>)}
                            </span>
                        </span>
                    </div>
                )}
            </div>

            {/* Admin-defined fields, last so they never push the everyday ones
                down the page. Renders nothing until someone defines one. */}
            <CustomFieldValues entity="Project" entityId={project.id} editable={editable} />
        </div>
    );
};

export default DetailsDrawer;
